// MA-15: on-reconnect outbox drain.
//
// When persistAttemptLocalFirst() fails the cloud write (or persistEnabled
// was false during an offline session), the attempt lands only in the
// localStorage mirror. This module drains that mirror to the cloud on the
// next verified-online transition.
//
// Key invariants:
//  1. Re-stamps childId with canonicalStudentId before uploading — local
//     attempts are stored with the LOCAL child UUID, but cloud FK/RLS
//     require the canonical Supabase students.id.
//  2. event_key is recomputed by logAttempt/logMilestoneEvent's toRow()
//     (which calls attemptEventKey/milestoneEventKey using the re-stamped
//     childId). The idempotency upsert (DO NOTHING on conflict) makes
//     repeated drains safe.
//  3. hasRealSession() guard: drain is a no-op without an auth session.
//  4. Circuit-breaker guard: drain skips while the breaker is open.
//  5. Draining flag: at most one drain runs at a time; concurrent triggers
//     (rapid reconnect oscillation) are silently dropped.
//  6. MA-374: _drainMilestones uploads every locally-mirrored milestone
//     type (session_completed, story_unlocked, story_read, book_opened,
//     favorite_set, favorite_unset, session_exit (MA-331), session_feedback
//     (MA-624), badge_earned (MA-892) — see localMilestoneStore's SCOPE comment for which types
//     are mirrored).
//     book_impression is never mirrored (cloud-only write,
//     DayCompleteCelebration) so it's never a drain candidate. Re-uploading
//     an already-synced row is a safe no-op:
//     milestoneEventKey is deterministic per (childId, eventType, slug[,
//     runId]) and the cloud upsert is DO NOTHING on event_key conflict.
//
// MA-111: a stranded attempt (its logAttempt retries exhausted mid-session)
// previously had NO retry path until the next app mount or a real browser
// online/offline DOM event (reachability.ts) — a Supabase-only blip that
// never flips navigator.onLine could sit un-synced for the rest of the tab's
// life. schedulePeriodicDrain() adds a bounded periodic + tab-foreground
// re-drain trigger. Bounding the PAYLOAD (not just the trigger) matters too:
// both _drainAttempts and _drainMilestones consult a per-child confirmed-key
// SET (localAttemptStore.loadUnconfirmedAttempts /
// localMilestoneStore.isMilestoneConfirmed) so a healthy periodic tick
// re-reads the local mirror but re-uploads nothing already confirmed.
//
// MA-376: this used to be a single max-confirmed TIMESTAMP cursor
// (`row.timestamp > cursor`). That silently and PERMANENTLY dropped rows on
// (a) a device clock set backward (a new row's timestamp lands before the
// cursor and never satisfies `> cursor`) and (b) two rows sharing one
// millisecond where only the first confirms (the cursor lands exactly ON
// that shared timestamp, so the second row's `> cursor` compare is false
// forever too). Confirmed-ness is now tracked by each row's IDENTITY KEY
// (see attemptDedupKey / milestoneDedupKey) instead of timestamp ordering,
// which makes both cases impossible — "already uploaded" is a fact about a
// row, never about its position relative to a moving cursor.

import { isCircuitOpen } from "./networkCircuitBreaker";
import { hasRealSession } from "@/lib/persistence";
import {
  loadUnconfirmedAttempts,
  markAttemptsConfirmed,
} from "./localAttemptStore";
import {
  loadLocalMilestones,
  isMilestoneConfirmed,
  markMilestonesConfirmed,
} from "./localMilestoneStore";
import { logAttempt } from "./attemptLogger";
import { logMilestoneEvent } from "./milestoneLogger";
import { drainPendingSessions } from "./sessionLogger";
import type { ChildAttempt } from "@/adaptation/types";
import type { MilestoneEvent } from "./milestoneLogger";

// Sequential batch size: check isCircuitOpen() between batches.
// Conservative enough to avoid API flood; idempotency makes re-runs safe.
const DRAIN_BATCH_SIZE = 25;

// At most one drain at a time across all callers (reconnect oscillation guard).
let draining = false;

interface DrainOpts {
  /** Local localStorage store key (app.activeChildId). */
  localChildId: string;
  /** Canonical cloud students.id (FK/RLS target). */
  canonicalStudentId: string;
  /** Injectable for unit tests. Defaults to the real logAttempt. */
  _logAttempt?: (a: ChildAttempt) => Promise<boolean>;
  /** Injectable for unit tests. Defaults to the real logMilestoneEvent. */
  _logMilestoneEvent?: (e: MilestoneEvent) => Promise<boolean>;
  /** Injectable for unit tests. Defaults to the real hasRealSession. */
  _hasRealSession?: () => Promise<boolean>;
  /** MA-377: injectable for unit tests. Defaults to the real
   *  drainPendingSessions. No localChildId/canonicalStudentId re-stamp
   *  needed — each queued session already carries its own canonical
   *  studentId (resolved before saveSession was ever called). */
  _drainSessions?: () => Promise<void>;
}

/** Drain the local localStorage mirror to the cloud.
 *  Safe to call on every verified-online transition — idempotent via MA-14
 *  event_key upsert. No-op when offline, unauthenticated, or already draining. */
export async function drainOutbox(opts: DrainOpts): Promise<void> {
  if (draining) return;
  if (isCircuitOpen()) return;

  const sessionCheck = opts._hasRealSession ?? hasRealSession;
  if (!(await sessionCheck())) return;

  draining = true;
  try {
    await _drainAttempts(opts);
    await _drainMilestones(opts);
    const drainSessionsFn = opts._drainSessions ?? drainPendingSessions;
    await drainSessionsFn();
  } finally {
    draining = false;
  }
}

async function _drainAttempts(opts: DrainOpts): Promise<void> {
  // MA-376: candidates are every row NOT YET in the confirmed-key set,
  // regardless of timestamp order — a healthy periodic tick (nothing new,
  // nothing stuck) sees rows.length===0 here and returns immediately.
  const rows = loadUnconfirmedAttempts(opts.localChildId, /* limit= */ 1000);
  if (rows.length === 0) return;

  const logFn = opts._logAttempt ?? logAttempt;

  for (let i = 0; i < rows.length; i += DRAIN_BATCH_SIZE) {
    if (isCircuitOpen()) break;
    const batch = rows.slice(i, i + DRAIN_BATCH_SIZE);
    const newlyConfirmed: ChildAttempt[] = [];
    for (const row of batch) {
      if (!row.runId || !row.itemId) {
        // Malformed rows can never sync — mark confirmed so they aren't
        // retried forever, same posture as a successful upload.
        newlyConfirmed.push(row);
        continue;
      }
      // Re-stamp with canonical id. toRow() inside logAttempt recomputes
      // event_key from the re-stamped childId, so the upsert key is correct.
      const confirmed = await logFn({ ...row, childId: opts.canonicalStudentId });
      if (confirmed) newlyConfirmed.push(row);
    }
    if (newlyConfirmed.length > 0) markAttemptsConfirmed(opts.localChildId, newlyConfirmed);
  }
}

// MA-374: every event type appendLocalMilestone is ever called with (see
// localMilestoneStore.ts's SCOPE comment) — kept as an explicit allowlist
// (not "everything in the store") so a future event type is drained only
// once someone deliberately adds it here, mirroring it locally first.
export const DRAINABLE_MILESTONE_TYPES: ReadonlySet<string> = new Set([
  "session_completed",
  "story_unlocked",
  "story_read",
  "book_opened",
  "favorite_set",
  "favorite_unset",
  "session_exit",
  "session_feedback", // MA-624 — mirrored locally by SessionRecap's onChange (see appendLocalMilestone call there), same pattern as session_exit.
  "badge_earned", // MA-892 — mirrored locally first by badges/persistence.ts latchBadges (one row per badge record, never evicted).
  "confirm_attempt", // MA-300 — mirrored locally by confirmAttemptTracker.emitConfirmAttempt BEFORE the cloud write, same pattern as session_feedback (0054 pending).
]);

async function _drainMilestones(opts: DrainOpts): Promise<void> {
  // loadLocalMilestones without a `since` filter returns ALL stored milestones.
  // MA-892: no row limit — the mirror holds CAP rows of the other types PLUS
  // every badge row (trimToCap exempts them), so a 500-row window could leave
  // an old offline badge row un-drained behind 500 newer rows.
  const rows = loadLocalMilestones(opts.localChildId, undefined, Number.POSITIVE_INFINITY);
  // MA-376: skip rows already confirmed-synced on a prior drain — bounds a
  // healthy periodic tick's cost the same way _drainAttempts' confirm-set
  // does (previously every drain re-uploaded the ENTIRE mirror every time).
  const toUpload = rows.filter(
    (e) => DRAINABLE_MILESTONE_TYPES.has(e.eventType) && !isMilestoneConfirmed(opts.localChildId, e),
  );
  if (toUpload.length === 0) return;

  const logFn = opts._logMilestoneEvent ?? logMilestoneEvent;

  for (let i = 0; i < toUpload.length; i += DRAIN_BATCH_SIZE) {
    if (isCircuitOpen()) break;
    const batch = toUpload.slice(i, i + DRAIN_BATCH_SIZE);
    const newlyConfirmed: MilestoneEvent[] = [];
    for (const row of batch) {
      // Re-stamp childId; milestoneEventKey inside logMilestoneEvent recomputes
      // from the re-stamped childId so the upsert key is correct.
      const confirmed = await logFn({ ...row, childId: opts.canonicalStudentId });
      if (confirmed) newlyConfirmed.push(row);
    }
    if (newlyConfirmed.length > 0) markMilestonesConfirmed(opts.localChildId, newlyConfirmed);
  }
}

// ── MA-111: periodic + tab-foreground re-drain ──────────────────────────
//
// registerOnlineCallback (reachability.ts) only fires on a real browser
// online/offline DOM event. A Supabase-only blip that never flips
// navigator.onLine leaves a stranded attempt with no further retry for the
// rest of the tab's life. This adds a bounded periodic tick + an immediate
// re-drain when the tab becomes foregrounded (matches the visibilitychange
// idiom already used by useSessionLifecycle.ts). The interval SKIPS its tick
// while the tab is backgrounded (`document.hidden`) so it doesn't spend
// battery/data on an unattended device; the visibility listener catches the
// return-to-foreground moment instead. The confirmed-key set (above) keeps
// a healthy tick's cost negligible even at a short-ish interval.
export const PERIODIC_DRAIN_INTERVAL_MS = 5 * 60 * 1000;

interface VisibilityDoc {
  hidden: boolean;
  addEventListener: (type: "visibilitychange", fn: () => void) => void;
  removeEventListener: (type: "visibilitychange", fn: () => void) => void;
}

interface PeriodicDrainDeps {
  setInterval: (fn: () => void, ms: number) => ReturnType<typeof setInterval>;
  clearInterval: (id: ReturnType<typeof setInterval>) => void;
  /** Document-like object; absent in non-browser contexts (SSR/tests) — the
   *  visibility gate + foreground trigger become no-ops (interval still runs
   *  unconditionally) when omitted. */
  doc?: VisibilityDoc | undefined;
}

interface PeriodicDrainHandle {
  stop(): void;
}

function defaultDeps(): PeriodicDrainDeps {
  return {
    // ⭐P0 (2026-07-10, prod-down hotfix): these MUST be arrow-wrapped. A bare
    // `setInterval` reference stored on this object and invoked as
    // `deps.setInterval(...)` runs the native function with `this` = the deps
    // object -> "Illegal invocation" in every browser -> the app-mount hook
    // (useOutboxDrain) threw -> the WHOLE app crashed to the error boundary on
    // every route. Node's timers are not `this`-sensitive, so the unit tests
    // (and any injected-deps test) stayed green — only real browsers failed.
    setInterval: (fn: () => void, ms: number) => setInterval(fn, ms),
    clearInterval: (id: ReturnType<typeof setInterval>) => clearInterval(id),
    doc: typeof document === "undefined" ? undefined : (document as unknown as VisibilityDoc),
  };
}

/** Schedule a bounded periodic re-drain (+ drain-on-foreground) for one
 *  child. Returns a handle whose stop() clears the timer and unregisters
 *  the visibility listener — callers MUST call stop() on unmount/child-
 *  switch to avoid an interval leak. */
export function schedulePeriodicDrain(
  opts: DrainOpts,
  intervalMs: number = PERIODIC_DRAIN_INTERVAL_MS,
  deps: PeriodicDrainDeps = defaultDeps(),
): PeriodicDrainHandle {
  const tick = () => {
    if (deps.doc?.hidden) return;
    void drainOutbox(opts);
  };
  const id = deps.setInterval(tick, intervalMs);

  const onVisible = () => {
    if (!deps.doc || deps.doc.hidden) return;
    void drainOutbox(opts);
  };
  deps.doc?.addEventListener("visibilitychange", onVisible);

  return {
    stop() {
      deps.clearInterval(id);
      deps.doc?.removeEventListener("visibilitychange", onVisible);
    },
  };
}

// Test-only: reset the draining lock so tests can call drainOutbox repeatedly.
export const __test = {
  resetDrainingLock(): void {
    draining = false;
  },
  isDraining(): boolean {
    return draining;
  },
};
